import React, { useState, useEffect } from 'react';
import { ChevronRight, AlertTriangle, Shield, QrCode, Monitor, Send, UserPlus } from 'lucide-react';

const DashboardHome = ({ user, clients = [], staffList = [], consentForms = [], studios = [], onChangePage, onAddClient, onSendForm, onViewClient, onOpenPublicForm, onOpenStudioQR }) => {
    // Use user.studio_name if available, otherwise fallback to user.username (which now contains fullName or email)
    const displayGreetingName = user.username;

    // --- STATS CALCULATION ---
    const totalStaff = staffList.length;
    const totalClients = clients.length;

    // Appointments (Clients with dates)
    const totalAppointments = clients.filter(c => c.appt_date).length;

    // Signed vs Pending
    const signedCount = clients.filter(c => c.status === 'signed' || c.signature).length;
    const pendingCount = clients.filter(c => c.status === 'PENDING' || c.status === 'NOT SIGNED').length;

    // Super Admin Stat
    const totalStudios = studios.length;

    // Pie Chart Data (SIGNED Ratio)
    const pieData = [
        { label: 'SIGNED', value: signedCount, color: '#22C55E' },
        { label: 'PENDING', value: pendingCount, color: '#F59E0B' }
    ];

    // Calculate Circumference for SVG Stroke
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    let accumulatedPercent = 0;

    const handleViewForm = () => {
        if (consentForms.length === 0) {
            alert("No consent forms found. Please create one first.");
            return;
        }
        const url = `/?formId=${consentForms[0].id}&studio_id=${user?.studio_id || user?.id}`;
        window.open(url, '_blank');
    };

    return (
        <div className="space-y-8 animate-fade-in-up">
            {/* WELCOME HEADER */}
            <div className="flex flex-row justify-between items-center gap-4">
                <div className="min-w-0">
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-800 tracking-tight">Dashboard Overview</h2>
                    <p className="text-slate-500 mt-1 italic sm:not-italic truncate">Welcome back, <span className="text-[#666CFF] font-bold">{displayGreetingName}</span>. Here's what's happening.</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                    <button
                        onClick={handleViewForm}
                        className="bg-slate-800 text-white px-3 py-2 rounded-lg text-xs font-bold shadow hover:bg-slate-900 transition-all flex items-center gap-1.5 border border-slate-700/50"
                    >
                        <Monitor size={14} className="text-slate-400 shrink-0" />
                        <span className="hidden sm:inline">View Consent Form</span>
                        <span className="sm:hidden">Form</span>
                    </button>
                    <button
                        onClick={onOpenStudioQR}
                        className="bg-gradient-to-br from-indigo-600 to-blue-700 text-white px-3 py-2 rounded-lg text-xs font-bold shadow hover:opacity-90 transition-all flex items-center gap-1.5"
                    >
                        <QrCode size={14} className="text-indigo-200 shrink-0" />
                        <span className="hidden sm:inline">Studio QR</span>
                        <span className="sm:hidden">QR</span>
                    </button>
                    <button
                        onClick={onSendForm}
                        className="bg-white text-slate-700 border border-slate-200 px-3 py-2 rounded-lg text-xs font-bold hover:bg-slate-50 transition-all shadow-sm flex items-center gap-1.5"
                    >
                        <Send size={14} className="text-slate-400 shrink-0" />
                        <span className="hidden sm:inline">Send Form</span>
                        <span className="sm:hidden">Send</span>
                    </button>
                </div>
            </div>

            {/* 1. KEY STATS GRID (BOXES) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Stat Cards */}
                <StatCard
                    label={studios.length > 0 ? "Total Studios" : user.role === 'staff' ? "My Clients" : "Total Staff"}
                    value={studios.length > 0 ? totalStudios : user.role === 'staff' ? totalClients : totalStaff}
                    icon={studios.length > 0 ? "🏢" : "👥"}
                    trend={user.role === 'staff' ? "Active" : "+2 this month"}
                />
                <StatCard
                    label="Total Forms Sent"
                    value={totalClients} // Every client represents a potential form sent in this context
                    icon="📝"
                    trend="Active"
                />
                <StatCard
                    label="Appointments"
                    value={totalAppointments}
                    icon="📅"
                    trend="Upcoming"
                />
                <StatCard
                    label="Forms Signed"
                    value={signedCount}
                    icon="✅"
                    trend={`${Math.round((signedCount / (totalClients || 1)) * 100)}% Rate`}
                />
            </div>

            {/* HIGH-RISK SAFETY ALERTS */}
            {clients.filter(c => c.has_red_flags).length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-6 animate-pulse-subtle">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Shield className="text-red-600" size={24} />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-bold text-red-800 text-lg flex items-center gap-2">
                                <AlertTriangle size={18} />
                                High-Risk Forms Require Attention
                            </h3>
                            <p className="text-red-600 text-sm mt-1 mb-4">
                                {clients.filter(c => c.has_red_flags).length} client(s) have reported health conditions that need review.
                            </p>
                            <div className="space-y-2">
                                {clients.filter(c => c.has_red_flags).slice(0, 3).map(client => (
                                    <div
                                        key={client.id}
                                        className="bg-white border border-red-100 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4 hover:shadow-md transition cursor-pointer"
                                        onClick={() => onViewClient(client)}
                                    >
                                        <div className="flex items-center gap-3 w-full sm:w-auto">
                                            {client.id_photo ? (
                                                <img src={client.id_photo} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-red-200" />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold flex-shrink-0">
                                                    {client.first_name?.[0]}{client.last_name?.[0]}
                                                </div>
                                            )}
                                            <div>
                                                <p className="font-bold text-slate-800">{client.first_name} {client.last_name}</p>
                                                <p className="text-xs text-red-500">🚨 Review Required</p>
                                            </div>
                                        </div>
                                        <button className="w-full sm:w-auto bg-red-600 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-red-700 transition">
                                            Review Now
                                        </button>
                                    </div>
                                ))}
                            </div>
                            {clients.filter(c => c.has_red_flags).length > 3 && (
                                <button
                                    onClick={() => onChangePage('sentForms')}
                                    className="text-red-600 font-bold text-sm mt-3 hover:underline"
                                >
                                    View all {clients.filter(c => c.has_red_flags).length} high-risk forms →
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* 2. RECENT ACTIVITY TABLE */}
                <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
                    <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                        <h3 className="font-bold text-slate-700 text-lg">Recent Bookings</h3>
                        <button onClick={() => onChangePage('sentForms')} className="text-xs font-bold text-[#666CFF] hover:underline uppercase">View All</button>
                    </div>
                    <div className="flex-1 overflow-auto">
                        <table className="w-full text-left text-sm text-slate-600">
                            <thead className="bg-[#f8f9fa] text-xs uppercase font-bold text-slate-500">
                                <tr>
                                    <th className="px-6 py-4">Customer</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Date</th>
                                    <th className="px-6 py-4 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {clients.slice(0, 5).map(c => (
                                    <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-slate-700">{c.first_name} {c.last_name}</div>
                                            <div className="text-xs text-slate-400">{c.email}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {(c.status === 'signed' || c.signature) ? (
                                                <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide">SIGNED</span>
                                            ) : (
                                                <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide">PENDING</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 font-medium">{c.appt_date ? new Date(c.appt_date).toLocaleDateString() : 'No Date'}</td>
                                        <td className="px-6 py-4 text-right">
                                            <button onClick={() => onViewClient(c)} className="text-slate-400 hover:text-[#666CFF] transition"><ChevronRight size={18} /></button>
                                        </td>
                                    </tr>
                                ))}
                                {clients.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="p-12 text-center">
                                            <div className="flex flex-col items-center justify-center space-y-4">
                                                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-slate-300 text-3xl">
                                                    📁
                                                </div>
                                                <div>
                                                    <p className="text-slate-500 font-bold">No bookings found yet</p>
                                                    <p className="text-slate-400 text-xs">When you add customers or send forms, they will appear here.</p>
                                                </div>
                                                <button
                                                    onClick={onAddClient}
                                                    className="bg-[#666CFF] text-white px-4 py-2 rounded-lg text-xs font-bold shadow-md hover:bg-[#555BDD] transition"
                                                >
                                                    Add Your First Customer
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 3. PIE CHART SECTION */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col relative overflow-hidden">
                    <h3 className="font-bold text-slate-700 text-lg mb-6 z-10">Form Completion</h3>

                    <div className="flex-1 flex flex-col items-center justify-center z-10">
                        {/* SVG PIE CHART */}
                        <div className="relative w-48 h-48">
                            <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                                {pieData.map((slice, i) => {
                                    const percent = slice.value / (totalClients || 1);
                                    const strokeDasharray = `${percent * circumference} ${circumference}`;
                                    const strokeDashoffset = -accumulatedPercent * circumference;
                                    accumulatedPercent += percent;

                                    return (
                                        <circle
                                            key={i}
                                            r={radius}
                                            cx="50"
                                            cy="50"
                                            fill="transparent"
                                            stroke={slice.color}
                                            strokeWidth="12" // Donut style
                                            strokeDasharray={strokeDasharray}
                                            strokeDashoffset={strokeDashoffset}
                                            className="transition-all duration-1000 ease-out hover:stroke-width-16 cursor-pointer"
                                        />
                                    );
                                })}
                            </svg>
                            {/* Center Text */}
                            <div className="absolute inset-0 flex items-center justify-center flex-col">
                                <span className="text-3xl font-bold text-slate-800">{signedCount}</span>
                                <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">SIGNED</span>
                            </div>
                        </div>

                        {/* Legend */}
                        <div className="flex gap-6 mt-8">
                            {pieData.map(d => (
                                <div key={d.label} className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }}></div>
                                    <div className="text-xs font-bold text-slate-600 uppercase">{d.label} ({d.value})</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Background decorations */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2"></div>
                </div>
            </div>

            {/* EMPTY STATE - GET STARTED GUIDE */}
            {clients.length === 0 && (
                <div className="bg-gradient-to-r from-[#666CFF] to-[#8E8EFF] rounded-2xl p-8 text-white shadow-xl shadow-indigo-200/50 relative overflow-hidden">
                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                        <div className="max-w-xl">
                            <h3 className="text-2xl font-bold mb-2">Welcome to your new Dashboard! 🚀</h3>
                            <p className="text-white/80 mb-6">It looks like you're just getting started. Follow these simple steps to begin collecting professional consent forms from your clients.</p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                                        <span className="font-bold">Setup Forms</span>
                                    </div>
                                    <p className="text-xs text-white/70">Review your consent forms and setup agreement sections in settings.</p>
                                </div>
                                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                                        <span className="font-bold">Add Customers</span>
                                    </div>
                                    <p className="text-xs text-white/70">Click 'Add Customer' to send a digital form directly to their email or phone.</p>
                                </div>
                            </div>
                        </div>
                        <div className="hidden lg:block w-48 h-48 bg-white/10 rounded-full border-8 border-white/5 flex items-center justify-center text-8xl animate-float">
                            ✨
                        </div>
                    </div>
                    {/* Decorative Background Elements */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-indigo-400/20 rounded-full blur-2xl -translate-x-1/2 translate-y-1/2"></div>
                </div>
            )}
        </div>
    );
};

// Sub-component for Stat Cards
const StatCard = ({ label, value, icon, trend }) => (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 relative overflow-hidden group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
        <div className="flex justify-between items-start mb-4 relative z-10">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-xl group-hover:scale-110 transition-transform duration-300 text-[#666CFF]">
                {icon}
            </div>
            {trend && <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-[10px] font-bold uppercase">{trend}</span>}
        </div>
        <div className="relative z-10">
            <h4 className="text-3xl font-bold text-slate-800 mb-1 tracking-tight">{value}</h4>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">{label}</p>
        </div>

        {/* Decorative Background */}
        <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full opacity-5 group-hover:opacity-10 transition-opacity duration-300 blur-xl"></div>
    </div>
);

export default DashboardHome;
